/* buildBSLinesForCompany — Phase 3a chart-of-accounts builder for BS_LINES.

   Produces the per-company BS_LINES array from:
   - bsActualsMeta entries (one per leaf, carrying category/subcategory/role/display_order)
   - SYNTHETIC_BS_LINES.alwaysPresent (headers, subtotals, check)
   - SYNTHETIC_BS_LINES.moduleGated (currently empty post-Session-0; reserved
     for future non-revenue moduleGated entries)
   - revenue_module_mappings rows (Session 0): each row's bs_line_key drives
     either historical-line augmentation (if key is in bsActualsMeta) or
     synthetic emission (if key is NOT in bsActualsMeta)

   Output shape matches the current hardcoded BS_LINES in computeBS.js exactly
   (same fields in same order per line), so Phase 3b can swap the engine over
   without numeric drift. No engines consume this builder yet; Phase 3a only
   validates equivalence for Semitek.

   Inputs
   - companyId: integer (reserved for future per-tenant customization)
   - bsActualsMeta: object keyed by line_id, each value carrying
       { label, gl_name, category, gl_category, subcategory, role,
         display_order, module_link_ref?, cfs_id?, cfs_label? }
   - revModuleMappings: rows from revenue_module_mappings — Session 0 consumes
     this to drive deferred-revenue line wiring (replaces the static
     deferred_rev_ae_quota / deferred_rev_company_quota entries previously in
     SYNTHETIC_BS_LINES.moduleGated)
   - moduleContext: { aeQuotaEnabled, companyQuotaEnabled, ... }
*/

import { SYNTHETIC_BS_LINES } from './lineCategories';
import { implDrLinkedKey, implDrRefKey } from '../utils/implKeys';
import { ACCRUED_PAYROLL_REF, accruedPayrollLinkKey } from './accruedPayroll';   // bi-weekly payroll, step 3b
import { ACCRUED_COMMISSIONS_REF, accruedCommissionsLinkKey } from './accruedCommissions';   // commission ship 4
import { UNBILLED_AR_REF, unbilledArLinkKey } from './billingTerms';   // billing terms P2: the Unbilled-AR link (p)

// Session 0: per-module-type config for synthetic BS deferred-revenue line emission.
// refKey / cfsId / sortOrder values match the static moduleGated entries previously
// in SYNTHETIC_BS_LINES.moduleGated to preserve byte-identity for Semitek/Chris Test.
export const MODULE_TYPE_TO_BS_CONFIG = {
  ae_quota:      { refKey: 'defrev_ae_quota',      cfsId: 'chg_dr_ae_quota',      sortOrder: 610 },
  company_quota: { refKey: 'defrev_company_quota', cfsId: 'chg_dr_company_quota', sortOrder: 620 },
};

function moduleEnabledForBS(ctx, moduleType) {
  if (moduleType === 'ae_quota')      return !!ctx.aeQuotaEnabled;
  if (moduleType === 'company_quota') return !!ctx.companyQuotaEnabled;
  return false;
}

// ── The operating-cash rollup (total_clearing) member predicate ──────────────
// THE single definition of "this leaf is operating cash". It selects the members
// of the total_clearing rollup — which computeCFS reads as the sweep's cash basis
// (bv("total_clearing", m)) — and, as the complement, partitions the rest of
// current assets.
//
// ★ ROLE-based, not subcategory-based. `role` is the leaf's semantic
// classification (what it IS); `subcategory` is a presentation grouping (where it
// RENDERS). Keying the engine's cash basis on a display field meant a tenant whose
// cash accounts were classified role='cash' but not tagged subcategory='clearing'
// got an EMPTY rollup → $0 cash basis → a silently broken sweep. That coupling is
// why Session A had to hand-tag AE Model's six bank accounts.
//
// INERT for every current tenant: role ∈ {cash,clearing} and subcategory='clearing'
// select the IDENTICAL leaf set on cid=1/2/3 (7 leaves each) — proven by set-equality
// in verify-cash-basis-role-predicate.js, so the rollup sum, the sweep basis, and the
// whole convergence cascade are unchanged. The sweep target is excluded by its own
// role ('sweep_target'), exactly as before (subcategory='sweep').
const CASH_ROLLUP_ROLES = new Set(['cash', 'clearing']);

function isCashRollupMember(leaf) {
  return leaf.category === 'current_assets' && CASH_ROLLUP_ROLES.has(leaf.role);
}

// Leaf BS line `level`: rollup members are level 3 (nested inside the cash
// subsection), equity leaves are level 1 (no level-2 subsection), everything else
// is level 2. Driven by the SAME predicate as the partition so a leaf can never
// render nested while being summed elsewhere (or vice versa).
function leafLevel(leaf) {
  if (isCashRollupMember(leaf)) return 3;
  if (leaf.category === 'equity') return 1;
  return 2;
}

// Session A correction (AE Model): bs_actuals_meta entries keyed by a
// synthetic line id (e.g. total_clearing) may carry a `display_label` field
// to override the static SYNTHETIC_BS_LINES label. Used so AE Model renders
// "Total Cash" instead of "Total Clearing" while keeping the same engine
// line id. Backward-compatible: tenants without an override entry fall
// through to the static label unchanged.
function makeAlwaysPresentWithMeta(id, meta) {
  const base = { id, ...SYNTHETIC_BS_LINES.alwaysPresent[id] };
  const override = meta && meta[id];
  if (override && override.display_label) base.label = override.display_label;
  return base;
}

function makeTotalWithMeta(id, sumOf, meta) {
  // alwaysPresent[id] is { label, level, type:'total' } — spreading then
  // appending sumOf preserves the {id, label, level, type, sumOf} key order
  // used by the hardcoded BS_LINES totals.
  return { ...makeAlwaysPresentWithMeta(id, meta), sumOf };
}

function toLeafEntry(leaf) {
  // Emit role + subcategory + module_link_ref + asset_type_match so the BS,
  // CFS, and DA engines can:
  //   - find the rolling RE / YTD NI lines by role (Phase 3b removes the
  //     hardcoded "retained_earnings" / "net_income" id lookups)
  //   - resolve module_link rows via leaf.module_link_ref → IS_REF[refKey]
  //     (Phase 3b removes the if-chain in computeBS.js:149-156)
  //   - bucket fixed/intangible D&A by subcategory
  // ⛔ asset_type_match is NOT emitted. The Cp2 per-asset-routing arc deleted its only
  //   consumer (computeDA keys grossAdditions off the asset's stored bs_line_id), so this
  //   copied a field nothing read. The DATA still exists on 5 cid=3 leaves in
  //   bs_actuals_meta; dropping the emission removes that key from cid=3's rawData.bsLines
  //   and changes nothing on cid=8/36/40, which never carried it.
  // Phase 3a's strict-equality verifier no longer matches after this — the
  // hardcoded BS_LINES has no metadata fields. Snapshot diffs are now the
  // runtime correctness gate.
  const entry = {
    id: leaf.id,
    label: leaf.label,
    level: leafLevel(leaf),
  };
  if (leaf.role)              entry.role = leaf.role;
  if (leaf.subcategory)       entry.subcategory = leaf.subcategory;
  if (leaf.module_link_ref)   entry.module_link_ref = leaf.module_link_ref;
  return entry;
}

export function buildBSLinesForCompany(companyId, bsActualsMeta, revModuleMappings, moduleContext, implementationLines, accruedPayrollLink, commissionAccrual, unbilledArLink) {
  const meta = bsActualsMeta || {};
  const ctx = moduleContext || {};

  // Session 0: data-driven module-DR wiring.
  // For each revenue_module_mappings row with bs_line_key:
  //   - If bs_line_key exists in bs_actuals_meta: record a moduleLinkOverride
  //     so the historical meta entry's module_link_ref gets injected during
  //     metaLeaves construction (the BS engine's module_link driver then
  //     finds the right IS_REF source). The historical line is the BS
  //     destination — no synthetic duplicate.
  //   - If bs_line_key NOT in bs_actuals_meta: emit a synthetic moduleGated
  //     line. Replaces the static deferred_rev_ae_quota /
  //     deferred_rev_company_quota entries previously in SYNTHETIC_BS_LINES.moduleGated.
  // Semitek/Chris Test: bs_line_keys 'deferred_rev_ae_quota' / 'deferred_rev_company_quota'
  //   are not in their bs_actuals_meta → synthetics emit (byte-identical to pre-Session-0)
  // AE Model: bs_line_keys 'deferred_revenue_enterprise' / 'deferred_revenue_self_serv'
  //   ARE in bs_actuals_meta → historical lines used, no synthetic duplicate
  const moduleLinkOverrides = {};
  const dynDRSynthetics = [];
  for (const row of (revModuleMappings || [])) {
    if (!row.bs_line_key) continue;
    const cfg = MODULE_TYPE_TO_BS_CONFIG[row.module_type];
    if (!cfg) continue;
    if (!moduleEnabledForBS(ctx, row.module_type)) continue;
    moduleLinkOverrides[row.bs_line_key] = cfg.refKey;
    if (!(row.bs_line_key in meta)) {
      dynDRSynthetics.push({
        id: row.bs_line_key,
        label: 'Deferred Revenue - ' + (row.module_name || row.display_name || row.module_type),
        level: 2,
        category: 'current_liabilities',
        subcategory: 'deferred_revenue',
        role: 'deferred_revenue',
        display_order: cfg.sortOrder,
        module_link_ref: cfg.refKey,
        cfs_id: cfg.cfsId,
      });
    }
  }

  /* W-081 (2026-10-05): an enabled DEFERRED implementation line LINKED to the client's real deferred-revenue account
   * (implementation_lines.bs_line_key, utils/implKeys.js implDrLinkedKey) wires THAT leaf to the engine's balance -- the same
   * moduleLinkOverrides path the AE / CQ modules use (Mantra #7), with the per-line ref 'defrev_impl_<id>' (orchestrator's
   * buildBSIsRef serves it). The leaf keeps its history; the forecast is the engine's deferred balance; the orchestrator makes
   * NO synthetic row for a line wired here. A key not in bs_actuals_meta wires nothing (the line then books its synthetic and
   * P6 names the unresolved answer). 5th positional arg, the buildISLines 7th-arg precedent: every caller that omits it is
   * byte-identical by construction. A leaf another stream already links is not re-wired (the server refuses that state). */
  for (const l of (implementationLines || [])) {
    if (!l || l.enabled === false || l.recognition_mode !== 'deferred') continue;
    const key = implDrLinkedKey(l.id, l.bs_line_key);
    if (!key || !(key in meta) || key in moduleLinkOverrides) continue;
    moduleLinkOverrides[key] = implDrRefKey(l.id);
  }

  /* BI-WEEKLY PAYROLL, STEP 3b (2026-10-08): a bi-weekly company that ACCRUES payroll links ONE of its own accrued-liability
   * lines (companies.accrued_payroll_bs_line_key; stored Step-4 role 'accrued', with a label). That leaf is wired to the
   * engine's accrued-payroll balance through the same moduleLinkOverrides path -- ref 'accrued_payroll', served by the
   * orchestrator only where this builder wired it. The leaf keeps its history; NO synthetic line is ever made. One rule,
   * engines/accruedPayroll.js accruedPayrollLinkKey: not bi-weekly + accrues, no key, a key that is not one of THIS company's
   * accrued lines, or a leaf another stream already links -> nothing is wired (the pre-flight's P21 names it).
   * 6th positional arg { schedule, lineKey } (the 5th-argument precedent above): every caller that omits it is byte-identical
   * by construction. */
  {
    const key = accruedPayrollLinkKey(accruedPayrollLink, meta, moduleLinkOverrides);
    if (key) moduleLinkOverrides[key] = ACCRUED_PAYROLL_REF;
  }

  /* COMMISSION SHIP 4 (2026-10-09): a company that ACCRUES commissions links ONE of its own accrued-liability lines
   * (companies.accrued_commissions_bs_line_key) -- never the accrued-payroll account. That leaf is wired to the payroll engine's
   * accrued-commissions balance through the same moduleLinkOverrides path, ref 'accrued_commissions', served by the orchestrator
   * only where this builder wired it. One rule, engines/accruedCommissions.js accruedCommissionsLinkKey (ship 3's
   * commissionAccountRefusal + not already linked): anything else wires nothing (the pre-flight's P23 names it).
   * 7th positional arg (the answer, commissionAccrual.js commissionAccrualFromSettings): every caller that omits it is
   * byte-identical by construction. */
  {
    const key = accruedCommissionsLinkKey(commissionAccrual, meta, moduleLinkOverrides, accruedPayrollLink ? accruedPayrollLink.lineKey : null);
    if (key) moduleLinkOverrides[key] = ACCRUED_COMMISSIONS_REF;
  }

  /* BILLING TERMS P2 (2026-10-26): a company that links an Unbilled-AR line (ae_quota_settings.unbilled_ar_bs_line_key, set by the
   * super-admin link route) gets THAT current-asset line wired to the engine's Unbilled-AR balance through the same
   * moduleLinkOverrides path -- ref 'unbilled_ar', served by the orchestrator only where this builder wired it. One rule,
   * engines/billingTerms.js unbilledArLinkKey: the AE module runs, the key is an Other receivable / Other current asset line of
   * THIS company, and no other stream links it -- otherwise nothing is wired (the pre-flight's P25 names it).
   * 8th positional arg (engines/billingTerms.js unbilledArLinkFromSettings): every caller that omits it is byte-identical. */
  {
    const key = unbilledArLinkKey(unbilledArLink, meta, moduleLinkOverrides);
    if (key) moduleLinkOverrides[key] = UNBILLED_AR_REF;
  }

  // Gather all leaves: meta entries (augmented with module_link_ref overrides
  // from revMappings if not already set) + enabled module-gated statics +
  // dynamic DR synthetics derived from revMappings
  const metaLeaves = Object.entries(meta).map(([id, m]) => {
    const augmented = { id, ...m, _source: 'meta' };
    // Inject module_link_ref from revMappings only if the meta entry doesn't
    // already carry one (preserve existing > inference).
    if (id in moduleLinkOverrides && !augmented.module_link_ref) {
      augmented.module_link_ref = moduleLinkOverrides[id];
    }
    return augmented;
  });
  const moduleLeaves = SYNTHETIC_BS_LINES.moduleGated
    .filter(g => g.enabledCheck(ctx))
    .map(g => ({ ...g, _source: 'module_gated' }));
  const allModuleLeaves = [
    ...moduleLeaves,
    ...dynDRSynthetics.map(s => ({ ...s, _source: 'module_gated_dynamic' })),
  ];

  const allLeaves = [...metaLeaves, ...allModuleLeaves]
    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

  // Bucket leaves by category for section emission
  const byCategory = {
    current_assets: [],
    non_current_assets: [],
    current_liabilities: [],
    non_current_liabilities: [],
    equity: [],
  };
  // ★★ A LEAF THAT MATCHES NO BUCKET IS NAMED, NOT SWALLOWED.
  // This loop used to be a bare `if (byCategory[...]) push` with no else: a leaf whose
  // `category` was not one of the five engine buckets simply VANISHED from the balance
  // sheet — no row, no total, no error. That is the historical "dropped BS leaves"
  // mechanism: the wizard's Step-3 vocabulary emits SNAKE values ('fixed_assets',
  // 'intangible_assets', 'lt_liabilities') which are NOT engine categories, and Session A
  // had to hand-correct cid=3's seven asset rows after exactly this. inferBsMetaAll
  // normalises the snake onto an engine category, so the only way to arrive here is a
  // bs_actuals_meta blob written WITHOUT that normalisation — which is precisely the
  // regression worth hearing about.
  //
  // ⛔ IT STILL DROPS THE LEAF, DELIBERATELY. There is no correct bucket to guess at, and
  // inventing one would be the §E1 defect (a routing decision made by code instead of by
  // the operator's explicit category). Warn LOUDLY and move no number — the same posture
  // as buildISLines' orphan warn and buildCFSLines' empty-rollup warn.
  //
  // THE ONE LEGITIMATE NO-BUCKET ENTRY: a meta entry with NO ROLE is a display-label
  // carrier, not a GL leaf — `total_clearing` (AdminPage writes it purely to override the
  // rollup's caption) is the only one any tenant has. The exemption keys on the ABSENCE
  // OF A ROLE, the same predicate buildBsAssumptions uses to skip it (inferBsMeta.js), and
  // NOT on the literal id 'total_clearing' — a hardcoded name would be the §6 defect in a
  // guard written to prevent one. MEASURED across cid=3/8/36/40: 40 role-bearing leaves
  // with valid categories, 1 role-less carrier (total_clearing), 0 role-bearing leaves
  // with a bad category, 0 role-less leaves with a valid category.
  const unbucketedLeaves = [];
  for (const leaf of allLeaves) {
    if (byCategory[leaf.category]) byCategory[leaf.category].push(leaf);
    else if (leaf.role) unbucketedLeaves.push(leaf);
  }
  if (unbucketedLeaves.length > 0) {
    console.warn(
      '[buildBSLines] ' + unbucketedLeaves.length + ' balance-sheet leaf(s) carry a category ' +
      'that is not one of the engine buckets (' + Object.keys(byCategory).join(', ') + '), so they ' +
      'are ABSENT from the balance sheet entirely — no row, no total, and the BS check will be ' +
      'off by their balance. This usually means bs_actuals_meta was written without ' +
      'inferBsMetaAll, leaving the Step-3 snake category in place. Offending leaves: ' +
      unbucketedLeaves.map(l => l.id + " (role=" + l.role + ", category=" + l.category + ")").join(', ')
    );
  }

  const lines = [];

  // ─── ASSETS ──────────────────────────────────────────────────────────
  lines.push(makeAlwaysPresentWithMeta('assets_header', meta));
  lines.push(makeAlwaysPresentWithMeta('ca_header', meta));

  // Current Assets / Clearing subsection
  lines.push(makeAlwaysPresentWithMeta('clearing_header', meta));
  // ★ Both halves of this partition MUST use the same predicate: total_current_assets
  // sums ['total_clearing', ...otherCA], so a leaf appearing in both would be
  // DOUBLE-COUNTED into total assets and break the BS check.
  const clearingLeaves = byCategory.current_assets.filter(isCashRollupMember);
  for (const l of clearingLeaves) lines.push(toLeafEntry(l));
  lines.push(makeTotalWithMeta('total_clearing', clearingLeaves.map(l => l.id), meta));

  // Current Assets / everything that is not operating cash (A/R, prepaid, the sweep
  // target itself — role='sweep_target' is not a rollup role, so it lands here and is
  // counted once, exactly as before).
  const otherCA = byCategory.current_assets.filter(l => !isCashRollupMember(l));
  for (const l of otherCA) lines.push(toLeafEntry(l));
  lines.push(makeTotalWithMeta('total_current_assets', ['total_clearing', ...otherCA.map(l => l.id)], meta));

  // Fixed Assets (non_current_assets / fixed_assets subcategory)
  lines.push(makeAlwaysPresentWithMeta('fa_header', meta));
  const fixedAssets = byCategory.non_current_assets.filter(l => l.subcategory === 'fixed_assets');
  for (const l of fixedAssets) lines.push(toLeafEntry(l));
  lines.push(makeTotalWithMeta('total_fixed_assets', fixedAssets.map(l => l.id), meta));

  // Intangible Assets (non_current_assets / intangible_assets subcategory)
  lines.push(makeAlwaysPresentWithMeta('ia_header', meta));
  const intangAssets = byCategory.non_current_assets.filter(l => l.subcategory === 'intangible_assets');
  for (const l of intangAssets) lines.push(toLeafEntry(l));
  lines.push(makeTotalWithMeta('total_intangible_assets', intangAssets.map(l => l.id), meta));

  lines.push(makeTotalWithMeta('total_assets', ['total_current_assets', 'total_fixed_assets', 'total_intangible_assets'], meta));

  // ─── LIABILITIES ─────────────────────────────────────────────────────
  lines.push(makeAlwaysPresentWithMeta('liabilities_header', meta));
  lines.push(makeAlwaysPresentWithMeta('cl_header', meta));
  const cl = byCategory.current_liabilities;
  for (const l of cl) lines.push(toLeafEntry(l));
  lines.push(makeTotalWithMeta('total_current_liabilities', cl.map(l => l.id), meta));

  lines.push(makeAlwaysPresentWithMeta('lt_header', meta));
  const lt = byCategory.non_current_liabilities;
  for (const l of lt) lines.push(toLeafEntry(l));
  lines.push(makeTotalWithMeta('total_lt_liabilities', lt.map(l => l.id), meta));

  lines.push(makeTotalWithMeta('total_liabilities', ['total_current_liabilities', 'total_lt_liabilities'], meta));

  // ─── EQUITY ──────────────────────────────────────────────────────────
  lines.push(makeAlwaysPresentWithMeta('equity_header', meta));
  const eq = byCategory.equity;
  for (const l of eq) lines.push(toLeafEntry(l));
  lines.push(makeTotalWithMeta('total_equity', eq.map(l => l.id), meta));

  lines.push(makeTotalWithMeta('total_le', ['total_liabilities', 'total_equity'], meta));

  lines.push(makeAlwaysPresentWithMeta('check', meta));

  return lines;
}
